import {
	ActivatedRouteSnapshot,
	DetachedRouteHandle,
	RouteReuseStrategy,
	Routes,
} from '@angular/router';
import { EnableUpdatedSysAdminGuard } from './services/enable-updated-sys-admin.guard';
import { NavigationAuthGuardService } from './services/navigation-auth-guard.service';
import { SelectActiveRoleGuardService } from './services/select-active-role-guard.service';
import { AccessMainTabComponent } from './ui/access/access-main/access-main.component';
import { AddCompanyMainTabComponent } from './ui/add-company/add-company-main-tab.component';
import { AddDeviceGroupMainTabComponent } from './ui/add-device-group/add-device-group-main-tab.component';
import { AddPropertyMainTabComponent } from './ui/add-property/add-property-main-tab.component';
import { AddUserMainTabComponent } from './ui/add-user/add-user-main-tab.component';
import { AuthenticatedComponent } from './ui/authenticated/authenticated.component';
import { CompanyDetailMainTabComponent } from './ui/company/detail/company-detail-main-tab.component';
import { CompanyEntityDetailPlaceholderComponent } from './ui/company/detail/company-entity-detail-placeholder/company-entity-detail-placeholder.component';
import { CompanyPropertyDetailComponent } from './ui/company/detail/company-property-detail/company-property-detail.component';
import { CompanyDetailRoleDetailComponent } from './ui/company/detail/company-roles/company-roles-detail/company-roles-detail.component';
import { CompaniesMainTabComponent } from './ui/company/main/companies-main-tab.component';
import { DeviceDetailsComponent } from './ui/device/detail/device-details.component';
import { DeviceGroupMainTabComponent } from './ui/device/device-group-main-tab/device-group-main-tab.component';
import { DeviceMainTabComponent } from './ui/device/device-main-tab/device-main-tab.component';
import { AddDevicesToGroupComponent } from './ui/device/flows/add-devices-to-group/add-devices-to-group.component';
import { ForgotPasswordComponent } from './ui/forgot-password/forgot-password.component';
import { HomeComponent } from './ui/home/home.component';
import { LoginAuthenticationComponent } from './ui/login/login-authentication/login-authentication.component';
import { LoginMfaComponent } from './ui/login/login-mfa/login-mfa.component';
import { LoginSelectRoleComponent } from './ui/login/login-select-role/login-select-role.component';
import { ProfileComponent } from './ui/profile/profile.component';
import { PropertyDetailsComponent } from './ui/property/detail/property-details.component';
import { PropertyMainTabComponent } from './ui/property/property-main-tab/property-main-tab.component';
import { ClientMainTabComponent } from './ui/client/client-main-tab/client-main-tab.component';
import { ClientAboutMainTabComponent } from './ui/client/client-about-main-tab/client-about-main-tab.component';
import { AddClientMainTabComponent } from './ui/add-client/add-client-main-tab.component';
import { BillingMainTabComponent } from './ui/billing/billing-main-tab/billing-main-tab.component';
import { LearningMainTabComponent } from './ui/learning/learning-main-tab/learning-main-tab.component';
import { CreatePasswordComponent } from './ui/register-user/create-password/create-password.component';
import { RegisterUserComponent } from './ui/register-user/register-user.component';
import { CodeGenReportTableComponent } from './ui/reports/reports-table/code-gen-report-table/code-gen-report-table.component';
import { InventoryReportTableComponent } from './ui/reports/inventory-report-table/inventory-report-table.component';
import { ReportsComponent } from './ui/reports/reports.component';
import { ResetPasswordComponent } from './ui/reset-password/reset-password.component';
import { AddRoleComponent } from './ui/roles/add-role/add-role-main-tab.component';
import { RoleDetailAddUserComponent } from './ui/roles/role-detail/role-detail-add-user/role-detail-add-user-main-tab.component';
import { RoleDetailComponent } from './ui/roles/role-detail/role-detail.component';
import { RoleMainTabComponent } from './ui/roles/role-main-tab.component';
import { UserDetailComponent } from './ui/user/detail/user-detail.component';
import { UserMainTabComponent } from './ui/user/user-main-tab/user-main-tab.component';
import { AccessReportTableComponent } from './ui/reports/access-report-table/access-report-table.component';
import { KeyDetectionReportTableComponent } from './ui/reports/key-detection-report-table/key-detection-report-table.component';
import { PropertyReportTableComponent } from './ui/reports/property-report-table/property-report-table.component';
import { DeviceLookupComponent } from './ui/device/device-lookup/device-lookup.component';
import { UsageReportTableComponent } from './ui/reports/usage-report-table/usage-report-table.component';
import { AlertReportTableComponent } from './ui/reports/alert-report-table/alert-report-table.component';
import { AddDeviceNoteMainTabComponent } from './ui/add-device-note/add-device-note-main-tab.component';
import { BillingContractComponent } from './ui/billing/billing-contract/billing-contract.component';
import { BillingContractDetailsComponent } from './ui/billing/billing-contract-details/billing-contract-details.component';

export const routes: Routes = [
	{
		path: '',
		canActivate: [NavigationAuthGuardService],
		component: AuthenticatedComponent,
		children: [
			{ path: '', component: HomeComponent },
			{ path: 'home', redirectTo: '', pathMatch: 'full' },
			{ path: '', redirectTo: 'company', pathMatch: 'full' },
			{ path: 'company', component: CompaniesMainTabComponent },
			{
				path: 'company/:id/detail',
				component: CompanyDetailMainTabComponent,
				data: {
					reuseOnQueryParams: [
						'tabIndex',
						'usersTab',
						'propertyTab',
						'devicesTab',
					],
				},
				children: [
					{
						path: 'property/:propertyId/detail',
						component: CompanyPropertyDetailComponent,
						canActivate: [EnableUpdatedSysAdminGuard],
						data: {
							reuseOnQueryParams: ['tabIndex', 'usersTab', 'propertyTab'],
						},
					},
				],
			},
			{
				path: 'company/:companyId/detail/role/:id/detail',
				component: CompanyDetailRoleDetailComponent,
			},
			{
				path: 'company/:companyId/detail/user/:id/detail',
				component: UserDetailComponent,
			},
			{
				path: 'company/:companyId/detail/device/:id/detail',
				component: CompanyEntityDetailPlaceholderComponent,
				data: {
					title: $localize`:@@companyDeviceDetail-title:Device`,
					message: $localize`:@@companyDeviceDetail-placeholderMessage:Device details will be available in a later release.`,
					automationId: 'company-device-detail-placeholder',
				},
			},
			{
				path: 'company/:companyId/detail/device-group/:id',
				component: CompanyEntityDetailPlaceholderComponent,
				data: {
					title: $localize`:@@companyDeviceGroupDetail-title:Device Group`,
					message: $localize`:@@companyDeviceGroupDetail-placeholderMessage:Device group details will be available in a later release.`,
					automationId: 'company-device-group-detail-placeholder',
				},
			},
			{ path: 'properties', component: PropertyMainTabComponent },
			{ path: 'property-details/:id', component: PropertyDetailsComponent },
			{ path: 'clients', component: ClientMainTabComponent },
			{ path: 'clients/:id', component: ClientAboutMainTabComponent },
			{ path: 'add-client', component: AddClientMainTabComponent },
			{ path: 'devices', component: DeviceMainTabComponent },
			{ path: 'devices/:id', component: DeviceDetailsComponent },
			{ path: 'billing', component: BillingMainTabComponent },
			{ path: 'learning', component: LearningMainTabComponent },
			{ path: 'add-billingcontract', component: BillingContractComponent },
			{
				path: 'billingcontract-details/:action/:id',
				component: BillingContractDetailsComponent,
			},
			{ path: 'reports', component: ReportsComponent },
			{ path: 'reports/accessReport', component: AccessReportTableComponent },
			{
				path: 'reports/keyDetectionReport',
				component: KeyDetectionReportTableComponent,
			},
			{
				path: 'reports/code-gen-report',
				component: CodeGenReportTableComponent,
			},
			{
				path: 'reports/usage-report-table',
				component: UsageReportTableComponent,
			},
			{
				path: 'reports/device-report',
				component: InventoryReportTableComponent,
			},
			{
				path: 'reports/property-report-table',
				component: PropertyReportTableComponent,
			},
			{
				path: 'reports/alert-report-table',
				component: AlertReportTableComponent,
			},
			{ path: 'users', component: UserMainTabComponent },
			{ path: 'profile', component: ProfileComponent },
			{ path: 'user-detail/:id', component: UserDetailComponent },
			{ path: 'roles', component: RoleMainTabComponent },
			{ path: 'roleDetail/:id', component: RoleDetailComponent },
			{
				path: 'roleDetail/:id/add-user',
				component: RoleDetailAddUserComponent,
			},
			{ path: 'add-role', component: AddRoleComponent },
			{ path: 'add-company', component: AddCompanyMainTabComponent },
			{ path: 'add-property', component: AddPropertyMainTabComponent },
			{ path: 'add-user', component: AddUserMainTabComponent },
			{ path: 'add-device-group', component: AddDeviceGroupMainTabComponent },
			{ path: 'device-group/:id', component: DeviceGroupMainTabComponent },
			{ path: 'deviceGroup/:id/add', component: AddDevicesToGroupComponent },
			{ path: 'access', component: AccessMainTabComponent },
			{ path: 'device-lookup', component: DeviceLookupComponent },
			{ path: 'add-note', component: AddDeviceNoteMainTabComponent },
		],
	},
	{ path: 'login', component: LoginAuthenticationComponent },
	{ path: 'do-mfa', component: LoginMfaComponent },
	{
		path: 'select-active-role',
		canActivate: [SelectActiveRoleGuardService],
		component: LoginSelectRoleComponent,
	},
	{ path: 'forgot-password', component: ForgotPasswordComponent },
	{ path: 'reset-password', component: ResetPasswordComponent },
	{ path: 'register-user', component: RegisterUserComponent },
	{ path: 'create-password', component: CreatePasswordComponent },
];

/**
 * @description
 *
 * This retrieved from https://www.auroria.io/angular-route-reuse-strategy/
 * `shouldReuseRoute` altered to always return `false`
 *
 * This base route reuse strategy only reuses routes when the matched router configs are
 * identical. This prevents components from being destroyed and recreated
 * when just the fragment or query parameters change
 * (that is, the existing component is _reused_).
 *
 * This strategy does not store any routes for later reuse.
 *
 * Angular uses this strategy by default.
 *
 *
 * It can be used as a base class for custom route reuse strategies, i.e. you can create your own
 * class that extends the `BaseRouteReuseStrategy` one.
 * @publicApi
 */
export class BaseRouteReuseStrategy implements RouteReuseStrategy {
	/**
	 * Whether the given route should detach for later reuse.
	 * Always returns false for `BaseRouteReuseStrategy`.
	 */
	shouldDetach(route: ActivatedRouteSnapshot): boolean {
		return false;
	}

	/**
	 * A no-op; the route is never stored since this strategy never detaches routes for later re-use.
	 */
	store(
		route: ActivatedRouteSnapshot,
		detachedTree: DetachedRouteHandle
	): void {}

	/** Returns `false`, meaning the route (and its subtree) is never reattached */
	shouldAttach(route: ActivatedRouteSnapshot): boolean {
		return false;
	}

	/** Returns `null` because this strategy does not store routes for later re-use. */
	retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
		return null;
	}

	/**
	 * Determines if a route should be reused.
	 * Reloads when config or path params change. Routes may opt in via
	 * `data.reuseOnQueryParams` so tab-only query updates (e.g. tabIndex,
	 * usersTab, propertyTab) keep the component instance instead of recreating it.
	 *
	 * Allowlist is resolved from this snapshot or a descendant (deepest wins) so
	 * activating a child without its own allowlist (company → property shell) still
	 * reuses the opted-in parent instead of flashing companyDetailsReady=false.
	 */
	shouldReuseRoute(
		future: ActivatedRouteSnapshot,
		curr: ActivatedRouteSnapshot
	): boolean {
		if (future.routeConfig !== curr.routeConfig) {
			return false;
		}
		if (!shallowEqualParams(future.params, curr.params)) {
			return false;
		}

		const keys = findReuseOnQueryParams(future);
		if (!keys) {
			return false;
		}

		const changed = changedQueryKeys(future.queryParams, curr.queryParams);
		if (changed.length === 0) {
			return true;
		}

		return changed.every((k) => keys.includes(k));
	}
}

/** Deepest `data.reuseOnQueryParams` on this snapshot or its firstChild chain. */
function findReuseOnQueryParams(
	snapshot: ActivatedRouteSnapshot
): string[] | undefined {
	let current: ActivatedRouteSnapshot | null = snapshot;
	let found: string[] | undefined;
	while (current) {
		const keys = current.routeConfig?.data?.['reuseOnQueryParams'] as
			string[] | undefined;
		if (keys?.length) {
			found = keys;
		}
		current = current.firstChild;
	}
	return found;
}

function shallowEqualParams(
	a: Record<string, string> | null | undefined,
	b: Record<string, string> | null | undefined
): boolean {
	const left = a ?? {};
	const right = b ?? {};
	const aKeys = Object.keys(left);
	const bKeys = Object.keys(right);
	if (aKeys.length !== bKeys.length) {
		return false;
	}
	return aKeys.every((key) => left[key] === right[key]);
}

function changedQueryKeys(
	future: Record<string, string> | null | undefined,
	curr: Record<string, string> | null | undefined
): string[] {
	const futureParams = future ?? {};
	const currParams = curr ?? {};
	const keys = new Set([
		...Object.keys(futureParams),
		...Object.keys(currParams),
	]);
	return [...keys].filter((key) => futureParams[key] !== currParams[key]);
}
